import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Win10AdminLayout from "../layouts/Win10AdminLayout";
import api from "../services/api";

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL");
}

function safeNumber(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function KpiCard({ label, value, tone = "default" }) {
  const toneClass =
    tone === "warning"
      ? "border-amber-300 bg-amber-50 text-amber-900"
      : tone === "danger"
        ? "border-red-300 bg-red-50 text-red-900"
        : "border-slate-300 bg-white text-slate-900";

  return (
    <div className={`rounded border p-3 ${toneClass}`}>
      <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{safeNumber(value)}</p>
    </div>
  );
}

function EventTable({ title, rows, columns, emptyText }) {
  return (
    <div className="rounded border border-slate-300 bg-white p-4 shadow-sm">
      <h2 className="mb-3 text-base font-semibold text-slate-900">{title}</h2>
      {!Array.isArray(rows) || rows.length === 0 ? (
        <p className="text-sm text-slate-600">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-slate-100 text-left">
                {columns.map((column) => (
                  <th key={column.key} className="p-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id || `${row.at || ""}-${row.action || row.eventType || ""}`} className="border-b">
                  {columns.map((column) => (
                    <td key={column.key} className="p-2 align-top text-slate-800">
                      {column.render ? column.render(row) : String(row?.[column.key] || "-")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminTenantMonitorPage() {
  const { tenantId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  const companyNameFromState = String(location?.state?.companyName || "").trim();

  useEffect(() => {
    let active = true;
    async function loadMonitor() {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/admin/tenants/${tenantId}/monitor`);
        if (!active) return;
        setPayload(res?.data || null);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || "Tenant monitor ophalen mislukt.");
      } finally {
        if (active) setLoading(false);
      }
    }

    if (tenantId) {
      loadMonitor();
    } else {
      setLoading(false);
      setError("Geen tenantId in route.");
    }

    return () => {
      active = false;
    };
  }, [tenantId]);

  const tenant = payload?.tenant || {};
  const kpis = payload?.kpis || {};
  const streams = payload?.streams || {};

  const tenantLabel = useMemo(() => {
    const apiName = String(tenant?.companyName || "").trim();
    return apiName || companyNameFromState || tenantId || "-";
  }, [tenant?.companyName, companyNameFromState, tenantId]);

  return (
    <Win10AdminLayout>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tenant Monitor</h1>
          <p className="text-sm text-slate-600">
            Support/gezondheidsdashboard voor tenant: <strong>{tenantLabel}</strong>
          </p>
          <p className="text-xs text-slate-500">Tenant ID: {tenantId}</p>
        </div>
        <Link
          to="/admin/tenants"
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          Terug naar tenants
        </Link>
      </div>

      {loading ? <p className="text-slate-600">Laden...</p> : null}
      {error ? <p className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}

      {!loading && !error ? (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
            <KpiCard label="Total Workorders" value={kpis.totalWorkorders} />
            <KpiCard label="Open Workorders" value={kpis.openWorkorders} tone={safeNumber(kpis.openWorkorders) > 0 ? "warning" : "default"} />
            <KpiCard label="Completed Workorders" value={kpis.completedWorkorders} />
            <KpiCard label="Without Planning" value={kpis.workordersWithoutPlanning} tone={safeNumber(kpis.workordersWithoutPlanning) > 0 ? "warning" : "default"} />
            <KpiCard label="Without Hours" value={kpis.workordersWithoutHours} tone={safeNumber(kpis.workordersWithoutHours) > 0 ? "warning" : "default"} />
            <KpiCard label="Without Photos" value={kpis.workordersWithoutPhotos} tone={safeNumber(kpis.workordersWithoutPhotos) > 0 ? "warning" : "default"} />
            <KpiCard label="Overdue Workorders" value={kpis.overdueWorkorders} tone={safeNumber(kpis.overdueWorkorders) > 0 ? "danger" : "default"} />
            <KpiCard label="Total Users" value={kpis.totalUsers} />
            <KpiCard label="Active Users (7d)" value={kpis.activeUsersLast7Days} />
            <KpiCard label="Open Support Tickets" value={kpis.openSupportTickets} tone={safeNumber(kpis.openSupportTickets) > 0 ? "warning" : "default"} />
            <KpiCard label="Website Requests" value={kpis.publicWebsiteRequestsCount} />
            <KpiCard label="Failed Emails" value={kpis.failedEmailCount} tone={safeNumber(kpis.failedEmailCount) > 0 ? "danger" : "default"} />
            <KpiCard label="Moneybird Sync Errors" value={kpis.failedMoneybirdSyncCount} tone={safeNumber(kpis.failedMoneybirdSyncCount) > 0 ? "danger" : "default"} />
            <KpiCard label="API Errors 24h" value={kpis.apiErrorsLast24h} tone={safeNumber(kpis.apiErrorsLast24h) > 0 ? "danger" : "default"} />
          </div>

          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded border border-slate-300 bg-white p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Last Login At</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{formatDateTime(kpis.lastLoginAt)}</p>
            </div>
            <div className="rounded border border-slate-300 bg-white p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Last Activity At</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{formatDateTime(kpis.lastActivityAt)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <EventTable
              title="Laatste 10 auditlog events"
              rows={streams.latestAuditEvents}
              emptyText="Geen auditlog events gevonden."
              columns={[
                { key: "at", label: "Tijd", render: (row) => formatDateTime(row.at) },
                { key: "action", label: "Actie" },
                { key: "objectType", label: "Object" },
                { key: "severity", label: "Severity" },
                { key: "status", label: "Status" },
                { key: "actor", label: "Gebruiker" },
              ]}
            />

            <EventTable
              title="Laatste 10 API errors"
              rows={streams.latestApiErrors}
              emptyText="Geen API errors gevonden."
              columns={[
                { key: "at", label: "Tijd", render: (row) => formatDateTime(row.at) },
                { key: "action", label: "Actie" },
                { key: "method", label: "Method" },
                { key: "path", label: "Path" },
                { key: "code", label: "Code" },
                { key: "message", label: "Melding" },
              ]}
            />

            <EventTable
              title="Laatste 10 mail events"
              rows={streams.latestMailEvents}
              emptyText="Geen mail events beschikbaar."
              columns={[
                { key: "at", label: "Ontvangen", render: (row) => formatDateTime(row.at) },
                { key: "processedAt", label: "Verwerkt", render: (row) => formatDateTime(row.processedAt) },
                { key: "provider", label: "Provider" },
                { key: "eventType", label: "Event Type" },
                { key: "messageId", label: "Message ID" },
              ]}
            />
          </div>
        </>
      ) : null}
    </Win10AdminLayout>
  );
}
